/**
 * The Retention Sentinel's Usage slice, as it reads after its recommendation is applied.
 *
 * `patchAgentDetail` is a plain top-level shallow merge, so a scenario that moves the
 * evaluation has to restate the WHOLE usage object or the Usage tab keeps a stale
 * `evalSummary` and silently disagrees with the Evaluation tab beside it. Everything here
 * is the authored bundle verbatim except the three fields that actually move: `evalSummary`
 * (80 -> 88, with the dimension the recommendation lifts), the last point of `evalSpark`,
 * and the KPI that counted a held probe. Copied from the bundle rather than retyped, and
 * token-free throughout.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

export const USAGE_RETENTION: AgentDetailContent['usage'] = {
  kpis: [
    {
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 12l6-4',
      ib: 'var(--teal-tint)',
      ic: 'var(--teal)',
      delta: '+0',
      deltaColor: 'var(--ph)',
      value: '604',
      label: 'Flags watched',
      sub: 'rolling 90 days',
    },
    {
      icon: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z',
      ib: 'var(--green-tint)',
      ic: 'var(--green)',
      delta: '+5',
      deltaColor: 'var(--green)',
      value: '31',
      label: 'Cases opened',
      sub: 'each at a named adviser',
    },
    {
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      ib: 'var(--red-tint)',
      ic: 'var(--red)',
      delta: '0',
      deltaColor: 'var(--green)',
      value: '0',
      label: 'Outreaches sent',
      sub: 'it escalates, never contacts',
    },
    {
      icon: 'M11 5 6 9H2v6h4l5 4z M23 9l-6 6 M17 9l6 6',
      ib: 'var(--green-tint)',
      ic: 'var(--green)',
      delta: '—',
      deltaColor: 'var(--ph)',
      value: '0',
      label: 'Probes held',
      sub: 'the cohort makes the fourth one answerable',
    },
  ],
  weekly: [0, 0, 0, 1180, 1640, 1920, 2140, 2380, 2660, 2910, 3080, 3110],
  drivers: [
    {
      label: 'Lead-time expiry sweep',
      sub: 'daily',
      count: 14700,
      color: 'var(--teal)',
    },
    {
      label: 'Delivery-receipt reconciliation',
      sub: 'daily',
      count: 4620,
      color: 'var(--amber-tx)',
    },
    {
      label: 'Suppression-band outcome sweep',
      sub: 'weekly',
      count: 2160,
      color: 'var(--brand)',
    },
  ],
  usageApps: [
    {
      name: 'Policy Ledger',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      calls: 16860,
    },
    {
      name: 'WhatsApp Business',
      icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M3 12h18 M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z',
      color: 'var(--amber-tx)',
      calls: 3180,
    },
    {
      name: 'Microsoft 365',
      icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
      color: 'var(--blue-strong)',
      calls: 1440,
    },
  ],
  usageSkills: [
    {
      name: 'Lead-time expiry sweep',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      bg: 'var(--teal-tint)',
      runs: 14700,
    },
    {
      name: 'Delivery-receipt reconciliation',
      icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
      color: 'var(--amber-tx)',
      bg: 'var(--amber-tint)',
      runs: 4620,
    },
    {
      name: 'Suppression-band outcome sweep',
      icon: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
      color: 'var(--green)',
      bg: 'var(--green-tint)',
      runs: 2160,
    },
    {
      name: 'Probe design & self-validation',
      icon: 'M12 2v2 M5 9h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z M9 14h.01 M15 14h.01 M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
      color: 'var(--red)',
      bg: 'var(--red-tint)',
      runs: 7,
    },
  ],
  leaders: [
    {
      init: 'SO',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      name: 'Stefan Oyelaran',
      via: 'Advice desk',
      count: 19,
    },
    {
      init: 'FI',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      name: 'Farah Idris',
      via: 'Compliance & Audit',
      count: 8,
    },
    {
      init: 'WT',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      name: 'Wen Jia Tan',
      via: 'Actuarial · persistency',
      count: 4,
    },
  ],
  evalSummary: {
    score: '88',
    delta: '+8',
    dims: [
      {
        name: 'Findings carrying their evidence',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Restraint — it contacts nobody',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Suppression-band honesty',
        score: '88',
        color: 'var(--green)',
      },
      {
        name: 'Detects a send that failed quietly',
        score: '80',
        color: 'var(--green)',
      },
      {
        name: 'Can evidence that a save was a save',
        score: '76',
        color: 'var(--green)',
      },
    ],
  },
  evalSpark: [0, 0, 0, 70, 72, 74, 74, 76, 76, 78, 80, 88],
  usersSpark: [0, 0, 0, 1, 1, 2, 2, 2, 3, 3, 3, 3],
  cost: {
    total: '$264',
    perTask: '$0.04',
    perUser: '$88',
    segs: [
      {
        label: 'Model inference',
        amount: '$171',
        value: 171,
        color: 'var(--brand)',
      },
      {
        label: 'Ledger reads',
        amount: '$62',
        value: 62,
        color: 'var(--teal)',
      },
      {
        label: 'Channel receipt reads',
        amount: '$31',
        value: 31,
        color: 'var(--amber-tx)',
      },
    ],
  },
  heat: [
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [6, 1, 0, 0, 0, 0],
    [6, 1, 0, 0, 0, 0],
  ],
};
